import React from 'react';
import { Building2 } from 'lucide-react';
import { getCompanyLogoSrcset, getCompanyLogoDefaultSrc, getCompanyLogoSources } from '../utils/company/get-company-logo-url';
import { useLogoBackground } from '../hooks/useLogoBackground';

interface CompanyLogoProps {
  /** Company name/ID (used to generate logo URL) */
  companyName: string;
  /** Size in pixels (default: 40) */
  size?: number;
  /** Alt text for the image */
  alt?: string;
  /** Optional custom className for the container */
  className?: string;
  /** Style variant */
  variant?: 'default' | 'rounded' | 'square';
  /** Flag indicating if this is a tyomarkkinatori job */
  isTyomarkkinatori?: boolean;
  /** Image loading strategy - 'eager' for above-fold, 'lazy' for below-fold (default: 'lazy') */
  loading?: 'eager' | 'lazy';
  /** When true, skip logo color detection and always use default container background (avoids static vs hydrated mismatch, e.g. company header) */
  useDefaultBackgroundOnly?: boolean;
}

/**
 * Company Logo Component
 *
 * Displays a company logo. Logo URL is always generated from company name:
 * {BASE_URL}/logos/{kebab-company-name}-128.webp (and srcset 64/128/200).
 * Falls back to default jobcrawls logo on load error.
 *
 * Variants:
 * - default: Rounded with border and shadow (used in job cards)
 * - rounded: Simple rounded container (used in location header)
 * - square: No border radius
 */
export const CompanyLogo: React.FC<CompanyLogoProps> = ({
  companyName,
  size = 40,
  alt,
  variant = 'default',
  className = '',
  isTyomarkkinatori: isTyomarkkinatoriProp,
  loading = 'lazy',
  useDefaultBackgroundOnly = false,
}) => {
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  
  // Use prop if provided, otherwise fallback to checking companyName (for backward compatibility)
  const isTyomarkkinatori = React.useMemo(() => {
    if (isTyomarkkinatoriProp !== undefined) {
      return isTyomarkkinatoriProp;
    }
    const normalized = companyName?.toLowerCase().trim();
    return normalized === 'työmarkkinatori' || normalized === 'tyomarkkinatori';
  }, [companyName, isTyomarkkinatoriProp]);
  
  // Generate srcset for WebP logos (64w, 128w, 200w)
  const srcset = React.useMemo(() => getCompanyLogoSrcset(companyName, isTyomarkkinatori), [companyName, isTyomarkkinatori]);
  const defaultSrc = React.useMemo(() => getCompanyLogoDefaultSrc(companyName, isTyomarkkinatori), [companyName, isTyomarkkinatori]);
  
  const defaultLogoSrc = '/company-logo-default.svg';

  // Initialize state with default values (important for SSR)
  const [currentSrc, setCurrentSrc] = React.useState<string>(defaultSrc);
  const [currentSrcSet, setCurrentSrcSet] = React.useState<string>(srcset);
  const hasTriedFallback = React.useRef(false);

  // Reset when company changes
  React.useEffect(() => {
    setCurrentSrc(defaultSrc);
    setCurrentSrcSet(srcset);
    hasTriedFallback.current = false;
  }, [companyName, defaultSrc, srcset]);

  // Also check if image failed to load after a timeout
  React.useEffect(() => {
    if (!srcset || hasTriedFallback.current) return;
    
    const timeout = setTimeout(() => {
      if (imgRef.current && !hasTriedFallback.current) {
        // Check if image is in error state (naturalWidth/Height are 0 and complete is true)
        const img = imgRef.current;
        if (img.complete && img.naturalWidth === 0 && img.naturalHeight === 0) {
          console.warn('[CompanyLogo] Fallback (timeout): image failed to load, using default logo. URL was:', img.src);
          hasTriedFallback.current = true;
          img.srcset = '';
          img.src = defaultLogoSrc;
          setCurrentSrcSet('');
          setCurrentSrc(defaultLogoSrc);
        }
      }
    }, 2000); // Check after 2 seconds
    
    return () => clearTimeout(timeout);
  }, [srcset, defaultLogoSrc]);

  const handleError = React.useCallback(() => {
    // If WebP failed and we haven't tried the fallback yet, use default logo
    if (!hasTriedFallback.current && imgRef.current) {
      const failedSrc = imgRef.current.src;
      console.warn('[CompanyLogo] Fallback (onError): image failed to load, using default logo. URL was:', failedSrc, '| companyName:', companyName);
      hasTriedFallback.current = true;
      const img = imgRef.current;
      // Clear srcset first, then set src to default logo
      img.srcset = '';
      img.src = defaultLogoSrc;
      setCurrentSrcSet('');
      setCurrentSrc(defaultLogoSrc);
    }
  }, [defaultLogoSrc, companyName]);

  // Detect if logo needs a background color (skip when useDefaultBackgroundOnly so static and hydrated match)
  const { backgroundColor, needsBackground } = useLogoBackground(
    useDefaultBackgroundOnly ? undefined : (currentSrc !== defaultLogoSrc ? currentSrc : undefined)
  );

  const containerStyles = {
    default: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden shadow-sm',
    rounded: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm',
    square: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden',
  };

  // Get border radius based on variant
  const borderRadius = variant === 'square' ? '0' : variant === 'rounded' ? '0.75rem' : '1rem';

  // Special styling for Työmarkkinatori with purple background
  // If logo needs background and is not Tyomarkkinatori, apply detected background color
  const containerStyle = isTyomarkkinatori
    ? {
        width: size,
        height: size,
        padding: '0',
        backgroundColor: '#6B46C1', // Tyomarkkinatori purple
        borderRadius: borderRadius,
      }
    : {
        width: size,
        height: size,
        padding: '0',
        ...(needsBackground && backgroundColor ? { backgroundColor } : {}),
        borderRadius: borderRadius,
      };

  // Determine container className - remove default background if we're applying a custom one
  const containerClassName = isTyomarkkinatori
    ? ''
    : needsBackground && backgroundColor
    ? containerStyles[variant].replace(/bg-\w+-\d+\s+dark:bg-\w+-\d+/g, '').trim()
    : containerStyles[variant];

  return (
    <div
      className={`flex items-center justify-center ${containerClassName} ${className} relative`}
      style={containerStyle}
    >
      <img
        ref={imgRef}
        src={currentSrc}
        srcSet={currentSrcSet || undefined}
        sizes={currentSrcSet ? `${size}px` : undefined}
        alt={alt || `${companyName} logo`}
        width={size}
        height={size}
        className="w-full h-full object-contain"
        onError={handleError}
        onAbort={handleError}
        loading={loading}
        decoding="async"
        style={{
          background: 'transparent',
          borderRadius: borderRadius,
          aspectRatio: '1 / 1',
        }}
      />
    </div>
  );
};

/**
 * Company Logo with Gradient Fallback
 * 
 * Similar to CompanyLogo but with a gradient icon fallback instead of trying the default logo.
 * Used when you want a visual indicator that no logo is available.
 */
export const CompanyLogoWithIconFallback: React.FC<CompanyLogoProps> = ({
  companyName,
  size = 40,
  alt,
  className = '',
}) => {
  const [imgError, setImgError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const srcIndexRef = React.useRef(0);
  const sources = React.useMemo(
    () => getCompanyLogoSources(companyName).slice(0, -1), // Exclude default logo
    [companyName]
  );
  const [srcIndex, setSrcIndex] = React.useState(0);

  const clearLoadingTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const setLoadingTimeout = React.useCallback(() => {
    clearLoadingTimeout();
    // Set a timeout to stop loading if image doesn't load within 3 seconds
    timeoutRef.current = setTimeout(() => {
      const currentIndex = srcIndexRef.current;
      if (currentIndex < sources.length - 1) {
        const nextIndex = currentIndex + 1;
        srcIndexRef.current = nextIndex;
        setSrcIndex(nextIndex);
        setIsLoading(true);
        setLoadingTimeout();
      } else {
        setImgError(true);
        setIsLoading(false);
      }
    }, 3000);
  }, [sources.length]);

  const handleError = React.useCallback(() => {
    clearLoadingTimeout();
    const currentIndex = srcIndexRef.current;
    if (currentIndex < sources.length - 1) {
      const nextIndex = currentIndex + 1;
      srcIndexRef.current = nextIndex;
      setSrcIndex(nextIndex);
      setIsLoading(true);
      setLoadingTimeout();
    } else {
      setImgError(true);
      setIsLoading(false);
    }
  }, [sources.length, setLoadingTimeout]);

  const handleLoad = React.useCallback(() => {
    clearLoadingTimeout();
    setIsLoading(false);
  }, []);

  // Reset state when company changes
  React.useEffect(() => {
    srcIndexRef.current = 0;
    setSrcIndex(0);
    setImgError(false);
    setIsLoading(true);
    setLoadingTimeout();
    return () => {
      clearLoadingTimeout();
    };
  }, [companyName, setLoadingTimeout]);

  // Reset timeout when srcIndex changes
  React.useEffect(() => {
    srcIndexRef.current = srcIndex;
    if (isLoading) {
      setLoadingTimeout();
    }
    return () => {
      clearLoadingTimeout();
    };
  }, [srcIndex, isLoading, setLoadingTimeout]);

  if (imgError || sources.length === 0) {
    return (
      <div 
        className={`flex items-center justify-center bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900 dark:to-green-800 rounded-xl ${className}`}
        style={{ width: size, height: size }}
      >
        <Building2 size={size * 0.5} className="text-green-700 dark:text-green-300" />
      </div>
    );
  }

  return (
    <div
      className={`flex items-center justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm ${className} relative`}
      style={{ width: size, height: size, padding: '0' }}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div 
            className="animate-spin rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-green-500 dark:border-t-green-400"
            style={{
              width: size * 0.4,
              height: size * 0.4,
            }}
          />
        </div>
      )}
      <img
        src={sources[srcIndex]}
        alt={alt || `${companyName} logo`}
        width={size}
        height={size}
        className={`w-full h-full object-contain transition-opacity duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onError={handleError}
        onLoad={handleLoad}
        onAbort={handleError}
        loading="lazy"
        style={{
          background: 'transparent',
          borderRadius: '0.75rem', // rounded-xl
        }}
      />
    </div>
  );
};

